import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
// `getViewParameterValue` and `yieldToMainThread` are NOT exported by this
// theme's @theme/utilities (they exist in the older Horizon this block was
// authored against). Importing them made the whole module fail to evaluate,
// so `customElements.define` never ran and every click on the selector was
// silently dead. Defined locally to keep this file self-contained - it is
// loaded only on the wallet template, so nothing else can be affected.

/** @returns {string|null} the `view` query parameter, or null */
function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get('view');
}

/**
 * Yields to the main thread so long tasks do not block input.
 * @see https://web.dev/articles/optimize-long-tasks#scheduler-yield
 * @returns {Promise<void>}
 */
function yieldToMainThread() {
  // guarded: `scheduler` is absent on Safari/Firefox, where a bare reference throws
  if (typeof scheduler !== 'undefined' && 'yield' in scheduler) {
    // @ts-ignore - TypeScript doesn't recognize the yield method yet.
    return scheduler.yield();
  }

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
}
import { CartErrorEvent, CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';
// This theme's cart drawer, cart items and cart icon all listen on `document`
// for ThemeEvents.cartUpdate (dispatched as CartAddEvent from @theme/events).
// CartLinesUpdateEvent above comes from @shopify/events - a different class from
// a different module - so on its own it refreshes nothing, which is why the drawer
// stayed empty until a full page reload.
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} ShopifyVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {number|null} compare_at_price
 * @property {boolean} available
 * @property {string|null} option1
 * @property {string|null} option2
 * @property {string|null} option3
 * @property {{ id?: number }|null} [featured_media]
 * @property {{ id?: number }|null} [featured_image]
 */

/** @extends {Component} */
class SpecialSaleSelector extends Component {
  /** @type {ShopifyVariant[]} */
  #variants = [];

  /** @type {number} */
  #activeDeal = 2;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isAdding = false;

  /** @type {(() => void) | undefined} */
  #boundHandlePageShow;

  /** @type {((event: Event) => void) | undefined} */
  #boundHandleDrawerClose;

  connectedCallback() {
    super.connectedCallback();

    const variantsScript = this.querySelector('[data-variants-json]');
    if (variantsScript?.textContent) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error('[special-sale-selector] Failed to parse variants JSON', error);
      }
    }

    this.#activeDeal = Number(this.dataset.defaultDeal) || 2;
    if (!this.querySelector(`[data-deal="${this.#activeDeal}"]`)) {
      const firstCard = this.querySelector('[data-deal]');
      this.#activeDeal = Number(firstCard instanceof HTMLElement ? firstCard.dataset.deal : 2) || 2;
    }

    this.addEventListener('change', this.#handleChange, true);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    this.#boundHandlePageShow = () => {
      this.#restoreEngravingDrafts();
      this.#syncAllPersonalisationStates();
      this.#syncEngravingPreview();
    };
    this.#boundHandleDrawerClose = (event) => this.#handleDrawerClose(event);

    window.addEventListener('pageshow', this.#boundHandlePageShow);
    document.addEventListener('theme-drawer:close', this.#boundHandleDrawerClose);

    this.#restoreEngravingDrafts();
    this.#initializeDealUI();
    this.#resolveAllWalletVariants();
    this.#syncAllPersonalisationStates();
    this.#updateDealPrices();
    this.#syncPrimaryVariant();
    this.#syncEngravingPreview();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange, true);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);

    if (this.#boundHandlePageShow) {
      window.removeEventListener('pageshow', this.#boundHandlePageShow);
    }
    if (this.#boundHandleDrawerClose) {
      document.removeEventListener('theme-drawer:close', this.#boundHandleDrawerClose);
    }
  }

  #initializeDealUI() {
    const deal = this.#activeDeal;

    this.querySelectorAll('[data-deal]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const isActive = Number(card.dataset.deal) === deal;
      card.classList.toggle('deal-card--active', isActive);

      const wallets = card.querySelector('[data-deal-wallets]');
      const plusIcon = card.querySelector('[data-icon-plus]');
      const checkIcon = card.querySelector('[data-icon-check]');

      wallets?.classList.toggle('hidden', !isActive);
      plusIcon?.classList.toggle('hidden', isActive);
      checkIcon?.classList.toggle('hidden', !isActive);
    });

    this.#syncSwatchSelectionFromChecked();
  }

  #syncSwatchSelectionFromChecked() {
    this.querySelectorAll('.deal-wallet__option--swatches').forEach((fieldset) => {
      fieldset.querySelectorAll('.deal-wallet__swatch-wrapper').forEach((wrapper) => {
        const radio = wrapper.previousElementSibling;
        const isChecked = radio instanceof HTMLInputElement && radio.checked;
        wrapper.classList.toggle('is-selected', isChecked);
      });
    });
  }

  /**
   * @param {Event} [_event]
   */
  addDealToCart(_event) {
    return this.#addSelectedDealToCart();
  }

  /**
   * @param {{ deal?: string | number } | Event | null | undefined} data
   */
  selectDeal(data) {
    const deal = Number(
      data && typeof data === 'object' && 'deal' in data
        ? data.deal
        : data instanceof Event && data.target instanceof Element
          ? data.target.closest('[data-select-deal], [data-deal]')?.getAttribute('data-select-deal') ||
            data.target.closest('[data-deal]')?.getAttribute('data-deal')
          : data
    );
    if (!deal) return;

    const nextCard = this.querySelector(`[data-deal="${deal}"]`);
    const uiAlreadyActive = nextCard?.classList.contains('deal-card--active');
    if (deal === this.#activeDeal && uiAlreadyActive) return;

    this.#activeDeal = deal;
    this.dataset.activeDeal = String(deal);

    this.querySelectorAll('[data-deal]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const isActive = Number(card.dataset.deal) === deal;
      card.classList.toggle('deal-card--active', isActive);

      const wallets = card.querySelector('[data-deal-wallets]');
      const plusIcon = card.querySelector('[data-icon-plus]');
      const checkIcon = card.querySelector('[data-icon-check]');

      wallets?.classList.toggle('hidden', !isActive);
      plusIcon?.classList.toggle('hidden', isActive);
      checkIcon?.classList.toggle('hidden', !isActive);
    });

    this.#updateDealPrices();
    this.#syncPrimaryVariant();
    this.#syncAllPersonalisationStates();
    this.#syncSwatchSelectionFromChecked();
    this.#syncEngravingPreview();
  }

  /** @param {Event} event */
  #handleDrawerClose(event) {
    if (!(event.target instanceof Element)) return;
    if (!event.target.querySelector('cart-drawer-component')) return;

    requestAnimationFrame(() => {
      this.#syncAllPersonalisationStates();
    });
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const dealHeader = target.closest('.deal-card__header');
    if (!dealHeader) return;

    event.preventDefault();
    this.#selectDealFromHeader(dealHeader);
  };

  /** @param {Element} dealHeader */
  #selectDealFromHeader(dealHeader) {
    const dealCard = dealHeader.closest('[data-deal]');
    if (!(dealCard instanceof HTMLElement)) return;

    const deal = Number(dealCard.dataset.deal);
    if (deal) this.selectDeal({ deal });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const previewButton = target.closest('[data-engraving-preview-trigger]');
    if (previewButton) {
      const wallet = previewButton.closest('.deal-wallet');
      if (wallet instanceof HTMLElement) {
        this.#syncEngravingPreview(wallet, { scrollToGallery: true });
      }
      return;
    }

    const dealTrigger = target.closest('[data-select-deal], .deal-card__header');
    if (dealTrigger) {
      const dealCard = dealTrigger.closest('[data-deal]');
      const deal = Number(dealTrigger.getAttribute('data-select-deal') || dealCard?.getAttribute('data-deal'));
      if (deal) this.selectDeal({ deal });
      return;
    }

    const persButton = target.closest('.deal-wallet__pers-button');
    if (!persButton) return;

    const wallet = persButton.closest('.deal-wallet');
    if (!wallet) return;

    requestAnimationFrame(() => {
      this.#syncPersonalisationState(wallet);
    });
  };

  /**
   * @param {Element | null | undefined} walletEl
   * @param {{ clearTextWhenDisabled?: boolean }} [options]
   */
  #syncPersonalisationState(walletEl, options = {}) {
    if (!(walletEl instanceof HTMLElement)) return;

    const inputWrap = walletEl.querySelector('[data-personalisation-input-wallet]');
    const textInput = walletEl.querySelector('[data-personalisation-text-wallet]');
    const onRadio = walletEl.querySelector('[data-personalisation-toggle-wallet][value="on"]');

    if (!(inputWrap instanceof HTMLElement) || !(textInput instanceof HTMLInputElement) || !(onRadio instanceof HTMLInputElement)) {
      return;
    }

    const enabled = onRadio.checked;
    inputWrap.classList.toggle('hidden', !enabled);
    textInput.disabled = !enabled;

    if (!enabled && options.clearTextWhenDisabled === true) {
      textInput.value = '';
      this.#updateEngravingCounter(walletEl, textInput);
      this.#setEngravingError(walletEl, false);
    }
  }

  /** @param {HTMLElement} walletEl */
  #updateEngravingCounter(walletEl, textInput) {
    if (!(textInput instanceof HTMLInputElement)) return;

    const counter = walletEl.querySelector('[data-personalisation-counter-wallet]');
    if (counter) {
      const max = textInput.maxLength > 0 ? textInput.maxLength : 30;
      counter.textContent = `${textInput.value.length}/${max}`;
    }
  }

  #syncAllPersonalisationStates() {
    this.querySelectorAll('.deal-wallet').forEach((wallet) => {
      this.#syncPersonalisationState(wallet, { clearTextWhenDisabled: false });
    });
  }

  /**
   * @param {boolean} isLoading
   */
  #setAddToCartLoading(isLoading) {
    const section = this.closest('.product-information, .shopify-section');
    if (!section) return;

    section.querySelectorAll('add-to-cart-component').forEach((component) => {
      if (typeof component.setLoading === 'function') {
        component.setLoading(isLoading);
      }
    });

    section.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (isLoading) {
        button.dataset.loading = 'true';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.removeAttribute('data-added');
      } else {
        button.removeAttribute('data-loading');
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-option-selector-wallet]')) {
      this.#handleWalletVariantChange(target);
      return;
    }

    if (target.matches('[data-personalisation-toggle-wallet]')) {
      this.#handlePersonalisationToggle(target);
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-personalisation-text-wallet]')) return;

    const counter = target
      .closest('[data-personalisation-input-wallet]')
      ?.querySelector('[data-personalisation-counter-wallet]');

    if (counter) {
      const max = target.maxLength > 0 ? target.maxLength : 30;
      counter.textContent = `${target.value.length}/${max}`;
    }

    target.classList.remove('deal-wallet__personalisation-text--error');
    const wallet = target.closest('.deal-wallet');
    if (wallet) {
      this.#setEngravingError(wallet, false);
      this.#syncEngravingPreview(wallet);
    }
    this.#saveEngravingDrafts();
  };

  #initializeWallets() {
    this.querySelectorAll('.deal-wallet').forEach((wallet) => {
      this.#syncPersonalisationState(wallet, { clearTextWhenDisabled: false });
    });
  }

  #resolveAllWalletVariants() {
    this.querySelectorAll('.deal-wallet').forEach((wallet) => {
      this.#resolveWalletVariant(wallet);
    });
  }

  /** @param {HTMLInputElement} input */
  #handleWalletVariantChange(input) {
    const wrapper = input.closest('.deal-wallet__swatch-wrapper');
    const fieldset = input.closest('.deal-wallet__option');
    const wallet = input.closest('.deal-wallet');

    fieldset?.querySelectorAll('.deal-wallet__swatch-wrapper').forEach((item) => {
      item.classList.toggle('is-selected', item === wrapper);
    });

    const label = fieldset?.querySelector('[data-wallet-label]');
    if (label instanceof HTMLElement) {
      const prefix = label.dataset.labelPrefix || 'Color:';
      const normalizedPrefix = /:\s*$/.test(prefix) ? `${prefix.trim()} ` : `${prefix}: `;
      label.textContent = `${normalizedPrefix}${input.value}`;
    }

    const bestsellerValue = this.dataset.bestsellerOptionValue;
    const badge = fieldset?.querySelector('[data-bestseller-badge]');
    if (badge instanceof HTMLElement && bestsellerValue && this.dataset.showBestsellerBadge === 'true') {
      const isBestseller = input.value === bestsellerValue;
      badge.classList.toggle('is-visible', isBestseller);
      badge.hidden = !isBestseller;
    }

    this.#resolveWalletVariant(wallet);

    const activeCard = this.querySelector(`[data-deal="${this.#activeDeal}"]`);
    if (wallet && activeCard?.contains(wallet)) {
      const walletIndex = Number(wallet.getAttribute('data-wallet'));
      if (walletIndex === 0) {
        this.#syncPrimaryVariant(input.dataset.optionValueId ?? '');
      }
      this.#updateDealPrices();
    }
  }

  /** @param {HTMLInputElement} input */
  #handlePersonalisationToggle(input) {
    const wallet = input.closest('.deal-wallet');
    if (!wallet) return;

    this.#syncPersonalisationState(wallet, { clearTextWhenDisabled: false });

    this.#resolveWalletVariant(wallet);
    this.#syncEngravingPreview(wallet);
    this.#updateDealPrices();
    this.#saveEngravingDrafts();

    const activeCard = this.querySelector(`[data-deal="${this.#activeDeal}"]`);
    if (activeCard?.contains(wallet)) {
      const walletIndex = Number(wallet.getAttribute('data-wallet'));
      if (walletIndex === 0) {
        this.#syncPrimaryVariant();
      }
    }
  }

  /** @param {Element | null | undefined} walletEl */
  #resolveWalletVariant(walletEl) {
    if (!(walletEl instanceof HTMLElement)) return null;

    const selectedValues = this.#getWalletSelectedValues(walletEl);
    let variant = this.#findVariant(selectedValues);

    if (!variant) {
      variant = this.#findVariantByCheckedInput(walletEl);
    }

    if (!variant && walletEl.dataset.resolvedVariantId) {
      variant = this.#variants.find((item) => String(item.id) === walletEl.dataset.resolvedVariantId);
    }

    if (variant) {
      walletEl.dataset.resolvedVariantId = String(variant.id);
      walletEl.dataset.resolvedPrice = String(variant.price);
    }

    return variant ?? null;
  }

  /** @param {Element} walletEl */
  #findVariantByCheckedInput(walletEl) {
    const checked = walletEl.querySelector('[data-option-selector-wallet]:checked');
    if (!(checked instanceof HTMLInputElement)) return undefined;

    const variantId = checked.dataset.variantId;
    if (!variantId) return undefined;

    const engravingPosition = Number(this.dataset.engravingOptionPosition);
    const enabled = walletEl.querySelector('[data-personalisation-toggle-wallet][value="on"]:checked');
    const withoutValue = this.dataset.withoutEngravingValue ?? '';
    const withValue = this.dataset.withEngravingValue ?? '';
    const engravingValue = enabled ? withValue : withoutValue;
    const colorValue = checked.value;

    const candidates = this.#variants.filter((variant) => {
      const options = [variant.option1, variant.option2, variant.option3];
      return options.includes(colorValue);
    });

    if (candidates.length === 0) {
      return this.#variants.find((item) => String(item.id) === variantId);
    }

    if (engravingPosition > 0 && engravingValue) {
      const matched = candidates.find(
        (variant) => variant[`option${engravingPosition}`] === engravingValue
      );
      if (matched) return matched;
    }

    if (withoutValue) {
      const matched = candidates.find((variant) =>
        [variant.option1, variant.option2, variant.option3].includes(withoutValue)
      );
      if (matched) return matched;
    }

    return candidates.sort((a, b) => a.price - b.price)[0];
  }

  /** @param {Element} walletEl */
  #getWalletSelectedValues(walletEl) {
    /** @type {string[]} */
    const values = [];

    walletEl.querySelectorAll('[data-option-selector-wallet]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        const optionEl = input.closest('[data-option-position]');
        const position = Number(optionEl?.getAttribute('data-option-position'));
        values[position - 1] = input.value;
      }
    });

    const engravingPosition = Number(this.dataset.engravingOptionPosition);
    if (engravingPosition > 0) {
      const enabled = walletEl.querySelector('[data-personalisation-toggle-wallet][value="on"]:checked');
      const withoutValue = this.dataset.withoutEngravingValue ?? '';
      const withValue = this.dataset.withEngravingValue ?? '';
      values[engravingPosition - 1] = enabled ? withValue : withoutValue;
    }

    return values;
  }

  /**
   * @param {string[]} selectedValues
   * @returns {ShopifyVariant | undefined}
   */
  #findVariant(selectedValues) {
    const candidates = this.#variants.filter((variant) => {
      const options = [variant.option1, variant.option2, variant.option3];

      for (let index = 0; index < selectedValues.length; index++) {
        const selected = selectedValues[index];
        if (selected == null || selected === '') continue;
        if (options[index] !== selected) return false;
      }

      return true;
    });

    if (candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0];

    const withoutValue = this.dataset.withoutEngravingValue;
    const engravingPosition = Number(this.dataset.engravingOptionPosition);

    if (withoutValue && engravingPosition > 0) {
      const preferred = candidates.find(
        (variant) => variant[`option${engravingPosition}`] === withoutValue
      );
      if (preferred) return preferred;
    }

    return candidates.sort((a, b) => a.price - b.price)[0];
  }

  #getActiveDealCard() {
    return this.querySelector(`[data-deal="${this.#activeDeal}"]`);
  }

  #getActiveWallets() {
    const card = this.#getActiveDealCard();
    return card ? Array.from(card.querySelectorAll('.deal-wallet')) : [];
  }

  #usesSplitEngravingPricing(deal = this.#activeDeal) {
    return deal === 2 && Boolean(this.dataset.engravingFeeVariantId);
  }

  /** @param {Element} walletEl */
  #walletHasEngraving(walletEl) {
    return Boolean(walletEl.querySelector('[data-personalisation-toggle-wallet][value="on"]:checked'));
  }

  /** @param {Element | null | undefined} walletEl */
  #resolveWithoutEngravingVariant(walletEl) {
    if (!(walletEl instanceof HTMLElement)) return null;

    const selectedValues = this.#getWalletSelectedValues(walletEl);
    const engravingPosition = Number(this.dataset.engravingOptionPosition);
    const withoutValue = this.dataset.withoutEngravingValue ?? '';

    if (engravingPosition > 0 && withoutValue) {
      selectedValues[engravingPosition - 1] = withoutValue;
    }

    let variant = this.#findVariant(selectedValues);

    if (!variant) {
      const checked = walletEl.querySelector('[data-option-selector-wallet]:checked');
      if (checked instanceof HTMLInputElement) {
        const colorValue = checked.value;
        const candidates = this.#variants.filter((item) => {
          const options = [item.option1, item.option2, item.option3];
          return options.includes(colorValue);
        });

        if (engravingPosition > 0 && withoutValue) {
          variant = candidates.find((item) => item[`option${engravingPosition}`] === withoutValue);
        }

        if (!variant && withoutValue) {
          variant = candidates.find((item) =>
            [item.option1, item.option2, item.option3].includes(withoutValue)
          );
        }

        if (!variant) {
          variant = candidates.sort((a, b) => a.price - b.price)[0];
        }
      }
    }

    return variant ?? null;
  }

  /** @param {Element} walletEl */
  #getEngravingFeeCents(_walletEl) {
    const feeCents = Number(this.dataset.engravingFeeCents);
    if (Number.isFinite(feeCents) && feeCents > 0) return feeCents;

    const withVariant = this.#resolveWalletVariant(_walletEl);
    const withoutVariant = this.#resolveWithoutEngravingVariant(_walletEl);

    if (withVariant && withoutVariant && withVariant.price > withoutVariant.price) {
      return withVariant.price - withoutVariant.price;
    }

    return 0;
  }

  /** @param {HTMLElement} card */
  #getDeal2CompareMode(card) {
    return (
      card.getAttribute('data-compare-mode') ||
      this.getAttribute('data-deal-2-compare-mode') ||
      'calculated'
    ).trim();
  }

  /**
   * @param {Element[]} wallets
   * @param {boolean} splitEngraving
   * @param {number} saleTotal
   */
  #getSingleWalletComparePrice(wallets, splitEngraving, saleTotal) {
    const firstWallet = wallets[0];
    if (!(firstWallet instanceof HTMLElement)) return 0;

    const variant = splitEngraving
      ? this.#resolveWithoutEngravingVariant(firstWallet)
      : this.#resolveWalletVariant(firstWallet);
    if (!variant) return 0;

    const unitCompare =
      variant.compare_at_price != null && variant.compare_at_price > 0
        ? variant.compare_at_price
        : variant.price;

    return unitCompare > saleTotal ? unitCompare : 0;
  }

  /**
   * @param {Element[]} wallets
   * @param {boolean} splitEngraving
   * @param {number} compareTotal
   * @param {number} saleTotal
   */
  #getCalculatedDeal2ComparePrice(wallets, splitEngraving, compareTotal, saleTotal) {
    if (compareTotal > saleTotal && compareTotal > 0) {
      return compareTotal;
    }

    if (wallets.length <= 1) return 0;

    const sumPrices = wallets.reduce((sum, wallet) => {
      const variant = splitEngraving
        ? this.#resolveWithoutEngravingVariant(wallet)
        : this.#resolveWalletVariant(wallet);
      let total = variant?.price ?? 0;

      if (splitEngraving && wallet instanceof HTMLElement && this.#walletHasEngraving(wallet)) {
        total += this.#getEngravingFeeCents(wallet);
      }

      return sum + total;
    }, 0);

    return sumPrices > saleTotal ? sumPrices : 0;
  }

  #updateDealPrices() {
    this.querySelectorAll('[data-deal]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const deal = Number(card.dataset.deal);
      const wallets = Array.from(card.querySelectorAll('.deal-wallet'));
      const compareEl = card.querySelector('[data-deal-compare-price]');
      const saleEl = card.querySelector('[data-deal-sale-price]');
      const splitEngraving = this.#usesSplitEngravingPricing(deal);

      let compareTotal = 0;
      let saleTotal = 0;

      wallets.forEach((wallet) => {
        const variant =
          splitEngraving && deal === 2
            ? this.#resolveWithoutEngravingVariant(wallet)
            : this.#resolveWalletVariant(wallet);
        if (!variant) return;

        compareTotal += variant.compare_at_price || variant.price;
        saleTotal += variant.price;
      });

      if (deal === 2 && wallets.length > 1) {
        const prices = wallets
          .map((wallet) => {
            const variant = splitEngraving
              ? this.#resolveWithoutEngravingVariant(wallet)
              : this.#resolveWalletVariant(wallet);
            return variant?.price ?? 0;
          })
          .filter((price) => price > 0);

        if (prices.length > 1) {
          saleTotal = prices.reduce((sum, price) => sum + price, 0) - Math.min(...prices);
        }

        if (splitEngraving) {
          let engravingFees = 0;
          wallets.forEach((wallet) => {
            if (!this.#walletHasEngraving(wallet)) return;
            engravingFees += this.#getEngravingFeeCents(wallet);
          });
          saleTotal += engravingFees;
        }
      }

      if (compareEl instanceof HTMLElement) {
        let displayCompare = 0;

        if (deal === 1) {
          if (this.getAttribute('data-deal-1-show-compare-price') === 'true' && compareTotal > saleTotal && compareTotal > 0) {
            displayCompare = compareTotal;
          }
        } else if (deal === 2) {
          const compareMode = this.#getDeal2CompareMode(card);

          if (compareMode === 'single_wallet_compare') {
            displayCompare = this.#getSingleWalletComparePrice(wallets, splitEngraving, saleTotal);
          } else {
            displayCompare = this.#getCalculatedDeal2ComparePrice(
              wallets,
              splitEngraving,
              compareTotal,
              saleTotal
            );
          }
        }

        if (displayCompare > 0) {
          compareEl.textContent = this.#formatPrice(displayCompare);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }

      if (saleEl instanceof HTMLElement && saleTotal > 0) {
        saleEl.textContent = this.#formatPrice(saleTotal);
      }
    });
  }

  /** @param {number} cents */
  #formatPrice(cents) {
    const format = this.#decodeMoneyFormat(this.dataset.moneyFormat || '£{{amount}}');
    const currency = this.dataset.currency || 'GBP';
    return formatMoney(cents, format, currency);
  }

  /**
   * Decodes HTML entities in money format strings from data attributes.
   * @param {string} format
   */
  #decodeMoneyFormat(format) {
    if (!format) return '£{{amount}}';
    const el = document.createElement('textarea');
    el.innerHTML = format;
    return el.value || format;
  }

  /** @param {string} [optionValueId] */
  #syncPrimaryVariant(optionValueId) {
    const wallets = this.#getActiveWallets();
    const primaryWallet = wallets[0];
    if (!primaryWallet) return;

    const checked = primaryWallet.querySelector('[data-option-selector-wallet]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    const variantId = primaryWallet.dataset.resolvedVariantId;
    const resolvedOptionValueId = optionValueId || checked.dataset.optionValueId || '';

    this.#updateProductFormVariantId(variantId);
    this.#selectVariantMediaImmediately(variantId);
    if (variantId) {
      this.#fetchUpdatedProductSectionByVariant(variantId);
    } else if (resolvedOptionValueId) {
      this.#fetchUpdatedProductSection(resolvedOptionValueId, checked);
    }
  }

  /** @param {string | undefined} variantId */
  #selectVariantMediaImmediately(variantId) {
    if (!variantId) return;

    const variant = this.#variants.find((item) => String(item.id) === String(variantId));
    const mediaId = variant?.featured_media?.id ?? variant?.featured_image?.id;
    if (!mediaId) return;

    document.dispatchEvent(
      new CustomEvent('product-media:select', {
        detail: {
          selector: this,
          mediaId: String(mediaId),
        },
      })
    );
  }

  /**
   * Updates the live engraving text on the main media image.
   * @param {Element | null} [walletEl]
   * @param {{ scrollToGallery?: boolean }} [options]
   */
  #syncEngravingPreview(walletEl = null, options = {}) {
    const activeCard = this.querySelector(`[data-deal="${this.#activeDeal}"]`);
    const primaryWallet = activeCard?.querySelector('[data-wallet="0"]');
    const wallet = walletEl instanceof HTMLElement ? walletEl : primaryWallet;
    if (!(wallet instanceof HTMLElement) || !activeCard?.contains(wallet)) return;

    const enabledInput = wallet.querySelector('[data-personalisation-toggle-wallet][value="on"]');
    const textInput = wallet.querySelector('[data-personalisation-text-wallet]');
    const enabled = enabledInput instanceof HTMLInputElement && enabledInput.checked;
    const text = textInput instanceof HTMLInputElement ? textInput.value : '';
    this.dataset.engravingPreviewDeal = activeCard.getAttribute('data-deal') || String(this.#activeDeal);
    this.dataset.engravingPreviewWallet = wallet.getAttribute('data-wallet') || '0';

    document.dispatchEvent(
      new CustomEvent('engraving-preview:update', {
        detail: {
          selector: this,
          enabled,
          text,
          scrollToGallery: options.scrollToGallery === true,
          x: Number(this.dataset.engravingPreviewX) || 50,
          y: Number(this.dataset.engravingPreviewY) || 70,
          fontSize: Number(this.dataset.engravingPreviewFontSize) || 22,
          color: this.dataset.engravingPreviewColor || '#e1dcd2eb',
        },
      })
    );
  }

  /** @param {string | undefined} variantId */
  #updateProductFormVariantId(variantId) {
    const section = this.closest('.product-information, .shopify-section');
    const variantInput = section?.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantId) {
      variantInput.value = variantId;
    }

    if (variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * @param {string} optionValueId
   * @param {HTMLInputElement} selectedInput
   */
  async #fetchUpdatedProductSection(optionValueId, selectedInput) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    const params = [`option_values=${optionValueId}`];
    const viewParam = getViewParameterValue();
    if (viewParam) params.unshift(`view=${viewParam}`);

    await this.#fetchProductHtml(
      `${productUrl.split('?')[0]}?${params.join('&')}`,
      optionValueId,
      selectedInput.value
    );
  }

  /** @param {string} variantId */
  async #fetchUpdatedProductSectionByVariant(variantId) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    const params = [`variant=${variantId}`];
    const viewParam = getViewParameterValue();
    if (viewParam) params.unshift(`view=${viewParam}`);

    await this.#fetchProductHtml(`${productUrl.split('?')[0]}?${params.join('&')}`, '', '', variantId);
  }

  /**
   * @param {string} requestUrl
   * @param {string} optionValueId
   * @param {string} selectedValue
   * @param {string} [requestedVariantId]
   */
  async #fetchProductHtml(requestUrl, optionValueId, selectedValue, requestedVariantId = '') {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: selectedValue ? [{ name: '', value: selectedValue }] : [],
        detail: { optionValueId },
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(requestUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Product update failed with status ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const variantScript = html.querySelector(
        'special-sale-selector [data-variants-json], variant-picker script[type="application/json"]'
      );
      let variant = null;
      if (variantScript?.textContent) {
        const parsed = JSON.parse(variantScript.textContent);
        if (Array.isArray(parsed)) {
          const responseVariantInput = html.querySelector('product-form-component input[name="id"]');
          const responseVariantId =
            requestedVariantId ||
            (responseVariantInput instanceof HTMLInputElement ? responseVariantInput.value : '');
          variant = parsed.find((item) => String(item.id) === String(responseVariantId)) ?? parsed[0] ?? null;
        } else {
          variant = parsed;
        }
      }

      deferredEventPromise.resolve({
        variant,
        detail: {
          html,
          productId: this.dataset.productId ?? '',
          sourceId: optionValueId,
          resource: variant,
          newProduct: null,
        },
      });

      await yieldToMainThread();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('[special-sale-selector] Failed to fetch updated product section', error);
      }
      deferredEventPromise.reject(error);
    }
  }

  /**
   * @param {object} result
   * @param {{ id: number, properties?: Record<string, string> }} walletItem
   * @returns {string | undefined}
   */
  #getAddedItemKey(result, walletItem) {
    const items = Array.isArray(result?.items) ? result.items : [];
    if (items.length === 0) return undefined;

    const walletProperties = walletItem.properties ?? {};

    const matched = items.find((item) => {
      if (item.variant_id !== walletItem.id) return false;

      const itemProperties = item.properties ?? {};
      const walletKeys = Object.keys(walletProperties);
      return walletKeys.every((key) => itemProperties[key] === walletProperties[key]);
    });

    return matched?.key ?? items[items.length - 1]?.key;
  }

  /**
   * @param {Array<Record<string, unknown>>} items
   * @param {string[]} sectionIds
   */
  async #postCartAdd(items, sectionIds) {
    const body = { items };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    return response.json();
  }

  /**
   * @param {Array<{ wallet: { id: number, quantity: number, properties?: Record<string, string> }, fee?: { id: number, quantity: number } }>} groups
   * @param {string[]} sectionIds
   */
  async #addCartItemGroupsSequentially(groups, sectionIds) {
    /** @type {Record<string, string> | undefined} */
    let sections;

    // Two round-trips, not two per wallet. An engraving fee needs
    // `parent_line_key`, which only exists once its wallet line has been created,
    // so the fees genuinely cannot go in the same request as their parents - but
    // all wallets can share one request, and all fees can share the next.
    const walletResult = await this.#postCartAdd(
      groups.map((group) => group.wallet),
      groups.some((group) => group.fee) ? [] : sectionIds
    );

    if (walletResult.status) return { error: walletResult, sections };
    if (walletResult.sections) sections = walletResult.sections;

    // Resolve each fee's parent from the single batched response. Wallet items
    // carry a distinct `_wallet_slot` property, so #getAddedItemKey can still
    // tell two lines of the same variant apart.
    /** @type {Array<Record<string, unknown>>} */
    const feeItems = [];
    for (const group of groups) {
      if (!group.fee) continue;

      const parentLineKey = this.#getAddedItemKey(walletResult, group.wallet);
      if (!parentLineKey) {
        return {
          error: { message: 'Could not link engraving fee to wallet line', status: 422 },
          sections,
        };
      }

      feeItems.push({
        id: group.fee.id,
        quantity: group.fee.quantity,
        parent_line_key: parentLineKey,
      });
    }

    if (feeItems.length === 0) return { sections };

    const feeResult = await this.#postCartAdd(feeItems, sectionIds);

    if (feeResult.status) return { error: feeResult, sections };
    if (feeResult.sections) sections = feeResult.sections;

    return { sections };
  }

  async #addSelectedDealToCart() {
    if (this.#isAdding) return;
    if (!this.#validateEngraving()) return;

    const section = this.closest('.product-information, .shopify-section') || document;
    const productForm = section.querySelector?.('product-form-component') || document.querySelector('product-form-component');
    const addToCartComponent = productForm?.querySelector('add-to-cart-component');
    const groups = this.#buildCartItemGroups();

    if (groups.length === 0) return;

    const upsellItems = this.#getSelectedUpsellItems(section);

    this.#isAdding = true;
    this.#setAddToCartLoading(true);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const eventTarget = productForm ?? document;
    const flatItems = [
      ...groups.flatMap((group) => {
        const items = [group.wallet];
        if (group.fee) items.push(group.fee);
        return items;
      }),
      ...upsellItems,
    ];
    const useSequentialAdds = groups.some((group) => Boolean(group.fee));

    eventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: flatItems.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      let result;

      if (useSequentialAdds) {
        const sequentialResult = await this.#addCartItemGroupsSequentially(groups, sectionIds);
        if (sequentialResult.error) {
          result = sequentialResult.error;
        } else {
          result = { sections: sequentialResult.sections };
        }
      } else {
        result = await this.#postCartAdd(
          groups.map((group) => group.wallet),
          sectionIds
        );
      }

      if (result.status) {
        eventTarget.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: result.description,
              errors: result.errors,
            },
          })
        );
        deferredEventPromise.reject(new Error(result.message || 'Add to cart failed'));
        return;
      }

      if (upsellItems.length > 0) {
        const upsellResult = await this.#postCartAdd(upsellItems, sectionIds);
        if (upsellResult.status) {
          eventTarget.dispatchEvent(
            new CartErrorEvent({
              error: upsellResult.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: upsellResult.description,
                errors: upsellResult.errors,
              },
            })
          );
          deferredEventPromise.reject(new Error(upsellResult.message || 'Add to cart failed'));
          return;
        }

        if (upsellResult.sections) {
          result.sections = upsellResult.sections;
        }
      }

      addToCartComponent?.animateAddToCart?.();

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'special-sale-selector',
          itemCount: flatItems.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      // Refresh the drawer, the line items and the cart count. `sections` lets
      // component-cart-items morph in place; without it, it falls back to a
      // fetch, so this is correct either way.
      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'special-sale-selector', {
          source: 'special-sale-selector',
          itemCount: flatItems.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );

      section?.querySelector('product-upsells')?.resetSelections?.();
      this.#clearEngravingDrafts();
    } catch (error) {
      console.error('[special-sale-selector] Add to cart failed', error);
      deferredEventPromise.reject(error);
    } finally {
      this.#setAddToCartLoading(false);
      this.#syncAllPersonalisationStates();
      this.#isAdding = false;
    }
  }

  /**
   * @param {Element | null | undefined} walletEl
   * @param {boolean} showError
   * @param {boolean} [shouldFocus]
   */
  #setEngravingError(walletEl, showError, shouldFocus = false) {
    if (!(walletEl instanceof HTMLElement)) return;

    const textInput = walletEl.querySelector('[data-personalisation-text-wallet]');
    const errorEl = walletEl.querySelector('[data-personalisation-error-wallet]');
    const message = this.dataset.engravingRequiredMessage || 'Please enter an engraving text.';

    if (textInput instanceof HTMLInputElement) {
      textInput.classList.toggle('deal-wallet__personalisation-text--error', showError);
      if (showError && shouldFocus) textInput.focus();
    }

    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = message;
      errorEl.classList.toggle('hidden', !showError);
    }
  }

  #validateEngraving() {
    const wallets = this.#getActiveWallets();
    let valid = true;
    let shouldFocusFirstError = true;

    wallets.forEach((wallet) => {
      const enabled = wallet.querySelector('[data-personalisation-toggle-wallet][value="on"]:checked');
      const textInput = wallet.querySelector('[data-personalisation-text-wallet]');
      if (!(enabled && textInput instanceof HTMLInputElement)) {
        this.#setEngravingError(wallet, false);
        return;
      }

      const trimmed = textInput.value.trim();
      const hasError = !trimmed;
      this.#setEngravingError(wallet, hasError, hasError && shouldFocusFirstError);

      if (hasError) {
        valid = false;
        shouldFocusFirstError = false;
      }
    });

    return valid;
  }

  #buildCartItemGroups() {
    /** @type {Array<{ wallet: { id: number, quantity: number, properties?: Record<string, string> }, fee?: { id: number, quantity: number } }>} */
    const groups = [];
    const wallets = this.#getActiveWallets();
    const splitEngraving = this.#usesSplitEngravingPricing();
    const feeVariantId = this.dataset.engravingFeeVariantId;

    wallets.forEach((wallet, walletIndex) => {
      // An engraved wallet must be ordered as the WITH-engraving variant so the order
      // records what the customer actually chose. The fee stays its own line and both
      // variants are identically priced, so the total is unchanged. Wallets without
      // engraving still resolve to the without-engraving variant exactly as before.
      const variant = splitEngraving && !this.#walletHasEngraving(wallet)
        ? this.#resolveWithoutEngravingVariant(wallet)
        : this.#resolveWalletVariant(wallet);
      if (!variant) {
        console.warn('[special-sale-selector] Could not resolve variant for wallet', wallet);
        return;
      }

      if (!variant.available) return;

      /** @type {Record<string, string>} */
      const properties = {};
      const engravingEnabled = this.#walletHasEngraving(wallet);
      const engravingInput = wallet.querySelector('[data-personalisation-text-wallet]');
      let hasEngravingText = false;

      if (engravingEnabled && engravingInput instanceof HTMLInputElement) {
        const propertyName = engravingInput.dataset.propertyName || this.dataset.engravingPropertyName || 'Engraving';
        const text = engravingInput.value.trim();
        if (text) {
          hasEngravingText = true;
          properties[propertyName] = text;
          if (splitEngraving) {
            properties._engraving_fee_split = '1';
            properties._wallet_slot = String(walletIndex);
          }
        }
      }

      /** @type {{ wallet: { id: number, quantity: number, properties?: Record<string, string> }, fee?: { id: number, quantity: number } }} */
      const group = {
        wallet: {
          id: variant.id,
          quantity: 1,
          ...(Object.keys(properties).length > 0 ? { properties } : {}),
        },
      };

      if (splitEngraving && hasEngravingText && feeVariantId) {
        group.fee = {
          id: Number(feeVariantId),
          quantity: 1,
        };
      }

      groups.push(group);
    });

    return groups;
  }

  /**
   * @param {Element | null | undefined} section
   * @returns {Array<{ id: number, quantity: number }>}
   */
  #getSelectedUpsellItems(section) {
    const productUpsells = section?.querySelector('product-upsells');
    if (!productUpsells || typeof productUpsells.getSelectedItems !== 'function') return [];
    return productUpsells.getSelectedItems();
  }

  #getEngravingDraftStorageKey() {
    const handle = this.dataset.productHandle;
    const productId = this.dataset.productId;
    const id = handle || productId || 'product';
    return `special-sale-engraving:${id}`;
  }

  /** @param {HTMLElement} walletEl */
  #getWalletDraftKey(walletEl) {
    const deal = walletEl.closest('[data-deal]')?.getAttribute('data-deal') ?? '0';
    const wallet = walletEl.getAttribute('data-wallet') ?? '0';
    return `${deal}-${wallet}`;
  }

  /** @returns {Record<string, { enabled: boolean, text: string }> | null} */
  #readEngravingDrafts() {
    try {
      const raw = localStorage.getItem(this.#getEngravingDraftStorageKey());
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  #saveEngravingDrafts() {
    if (!this.dataset.engravingEnabled && !this.dataset.engravingFeeVariantId) return;

    /** @type {Record<string, { enabled: boolean, text: string }>} */
    const draft = {};

    this.querySelectorAll('.deal-wallet').forEach((wallet) => {
      if (!(wallet instanceof HTMLElement)) return;

      const onRadio = wallet.querySelector('[data-personalisation-toggle-wallet][value="on"]');
      const textInput = wallet.querySelector('[data-personalisation-text-wallet]');
      if (!(onRadio instanceof HTMLInputElement) || !(textInput instanceof HTMLInputElement)) return;

      const key = this.#getWalletDraftKey(wallet);
      draft[key] = {
        enabled: onRadio.checked,
        text: textInput.value,
      };
    });

    try {
      const hasContent = Object.values(draft).some((entry) => entry.enabled || entry.text.trim());
      const storageKey = this.#getEngravingDraftStorageKey();

      if (!hasContent) {
        localStorage.removeItem(storageKey);
        return;
      }

      localStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      // Ignore storage failures (private browsing, quota, etc.)
    }
  }

  #restoreEngravingDrafts() {
    if (!this.dataset.engravingEnabled && !this.dataset.engravingFeeVariantId) return;

    const draft = this.#readEngravingDrafts();
    if (!draft) return;

    this.querySelectorAll('.deal-wallet').forEach((wallet) => {
      if (!(wallet instanceof HTMLElement)) return;

      const saved = draft[this.#getWalletDraftKey(wallet)];
      if (!saved) return;

      const onRadio = wallet.querySelector('[data-personalisation-toggle-wallet][value="on"]');
      const offRadio = wallet.querySelector('[data-personalisation-toggle-wallet][value="off"]');
      const textInput = wallet.querySelector('[data-personalisation-text-wallet]');

      if (onRadio instanceof HTMLInputElement && offRadio instanceof HTMLInputElement) {
        onRadio.checked = Boolean(saved.enabled);
        offRadio.checked = !saved.enabled;
      }

      if (textInput instanceof HTMLInputElement && typeof saved.text === 'string') {
        textInput.value = saved.text;
        this.#updateEngravingCounter(wallet, textInput);
      }
    });
  }

  #clearEngravingDrafts() {
    try {
      localStorage.removeItem(this.#getEngravingDraftStorageKey());
    } catch {
      // Ignore storage failures
    }
  }
}

if (!customElements.get('special-sale-selector')) {
  customElements.define('special-sale-selector', SpecialSaleSelector);
}

export default SpecialSaleSelector;
/* build kv-engraving-fix-1 */
